import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useImpersonateUserMutation } from 'store/services/admin/adminApi';
import { loginSuccess } from 'store/services/auth/authSlice';
import { RootState } from 'store';
import './ImpersonateUserModal.css';

interface ImpersonateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
}

const ImpersonateUserModal: React.FC<ImpersonateUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  username
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentToken = useSelector((state: RootState) => state.auth.token);

  const [reason, setReason] = useState<string>('');
  const [impersonateUser, { isLoading }] = useImpersonateUserMutation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isValid = reason.trim().length >= 10 && reason.trim().length <= 500;

  const handleSubmit = async () => {
    if (!isValid) return;

    try {
      const result = await impersonateUser({
        userId,
        body: {
          reason: reason.trim()
        }
      }).unwrap();

      // Backup current admin session (temporary storage for session restoration)
      if (currentToken && currentUser) {
        localStorage.setItem('adminTokenBackup', currentToken);
        localStorage.setItem('adminUserBackup', JSON.stringify(currentUser));
      }

      // Set impersonation flags
      localStorage.setItem('isImpersonating', 'true');
      localStorage.setItem('impersonatedUsername', username);
      localStorage.setItem('impersonatedUserId', userId.toString());

      // Switch to impersonated user session
      dispatch(loginSuccess({
        token: result.impersonationToken,
        user: result.user,
        isImpersonating: true
      }));

      toast.success(`Now impersonating ${username}. Session expires at ${new Date(result.expiresAt).toLocaleTimeString()}`);

      // Navigate to main app (the impersonation banner will show everywhere)
      navigate('/my-cards');
    } catch (error: any) {
      console.error('Failed to impersonate user:', error);
      toast.error(error.data?.message || 'Failed to start impersonation. Please try again.');
    }
  };

  const modalContent = (
    <div className="impersonate-user-modal-overlay" onClick={onClose}>
      <div className="impersonate-user-modal" onClick={(e) => e.stopPropagation()}>
        <div className="impersonate-user-modal-header">
          <h2 className="impersonate-user-modal-title">Impersonate User</h2>
          <button className="impersonate-user-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="impersonate-user-modal-content">
          <div className="info-banner">
            🎭 You are about to log in as <strong>{username}</strong> for support purposes.
          </div>

          <div className="user-info-section">
            <div className="info-row">
              <span className="info-label">Target User:</span>
              <span className="info-value username-value">{username}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Session Duration:</span>
              <span className="info-value">1 hour (auto-expires)</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="reason" className="form-label">
              Reason for Impersonation
              <span className="label-required">*</span>
            </label>
            <textarea
              id="reason"
              className="form-textarea"
              placeholder="Provide a clear reason for this impersonation session (10-500 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
              disabled={isLoading}
            />
            <CharacterCounter
              currentLength={reason.length}
              maxLength={500}
              warningThreshold={450}
            />
            {reason.length > 0 && reason.length < 10 && (
              <div className="validation-hint error">
                Reason must be at least 10 characters
              </div>
            )}
          </div>

          <div className="capabilities-section">
            <h3 className="section-subtitle">During Impersonation:</h3>
            <div className="capabilities-grid">
              <div className="capability-item">
                <span className="capability-icon">✅</span>
                <span>View all user data and collections</span>
              </div>
              <div className="capability-item">
                <span className="capability-icon">✅</span>
                <span>Perform actions as the user</span>
              </div>
              <div className="capability-item">
                <span className="capability-icon">✅</span>
                <span>Debug user-reported issues</span>
              </div>
              <div className="capability-item">
                <span className="capability-icon">⚠️</span>
                <span>Warning banner visible on all pages</span>
              </div>
              <div className="capability-item">
                <span className="capability-icon">🔒</span>
                <span>All actions are audit-logged</span>
              </div>
              <div className="capability-item">
                <span className="capability-icon">⏱️</span>
                <span>Session expires after 1 hour</span>
              </div>
            </div>
          </div>

          <div className="warning-box">
            <strong>Important:</strong> Your admin session will be safely stored.
            Click "Exit Impersonation" in the warning banner to return to your admin account.
          </div>
        </div>

        <div className="impersonate-user-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Starting Session...' : 'Start Impersonation'}
            color="purple"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isValid || isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ImpersonateUserModal;
